import { useState } from 'react';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import type { RoadmapNode } from "../../api/roadmap-service";

import { childKey, keysHoldingLessons, treeAtLevel } from "../../utils/roadmap-tree";
import { RoadmapBranch } from "./RoadmapBranch";

export type RoadmapStationPanelProps = {
    station: RoadmapNode;
    level: number;
    levelName: (level: number) => string;
    onOpenCourse: (node: RoadmapNode) => void;
    onNotes: (node: RoadmapNode) => void;
    onClose: () => void;
};

/**
 * Everything a station holds, as a small mindmap: its lesson groups, their lessons and the knowledge they require.
 * A station above the belt shows all of it, since the belt would hide it entirely.
 */
export function RoadmapStationPanel({ station, level, levelName, onOpenCourse, onNotes, onClose }: RoadmapStationPanelProps) {
    const [ collapsed, setCollapsed ] = useState<Set<string>>(() => new Set(keysHoldingLessons(station)));
    const above = station.level > level;
    const tree = (above ? station : treeAtLevel(station, level)) ?? station;

    function handleToggle(nodeKey: string) {
        const next = new Set(collapsed);

        if (next.has(nodeKey)) next.delete(nodeKey);
        else next.add(nodeKey);

        setCollapsed(next);
    }

    return (
        <Stack spacing={ 2 } sx={ { p: 3 } }>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={ 2 }>
                <Stack spacing={ 0.5 }>
                    <Typography component="h2" variant="inherit" sx={ { fontSize: 22, fontWeight: 800 } }>{ station.title }</Typography>
                    <Typography variant="body2" sx={ { color: '#4F5B67' } }>
                        { station.planned ? 'Planned — not in the catalogue yet · ' : '' }From { levelName(station.level) }
                    </Typography>
                </Stack>
                <IconButton aria-label="Close station" onClick={ onClose }><CloseIcon/></IconButton>
            </Stack>

            <Stack direction="row" spacing={ 1 } flexWrap="wrap">
                { station.courseId && !station.planned && (
                    <Button variant="contained" onClick={ () => onOpenCourse(station) } sx={ { borderRadius: '10px', fontWeight: 700 } }>
                        Open course
                    </Button>
                ) }
                { station.notes.length > 0 && (
                    <Button size="small" onClick={ () => onNotes(station) }>Required knowledge</Button>
                ) }
                <Button size="small" onClick={ () => setCollapsed(new Set()) }>Expand all</Button>
                <Button size="small" onClick={ () => setCollapsed(new Set(keysHoldingLessons(station))) }>Fold lessons</Button>
            </Stack>

            { /* The panel's title names the station, so the mindmap starts with what it holds */ }
            <Stack spacing={ 1 } sx={ { overflowX: 'auto', pb: 1 } } data-testid="roadmap-station-tree">
                { tree.children.map((child, index) => (
                    <RoadmapBranch key={ childKey('root', index) } node={ child } nodeKey={ childKey('root', index) } depth={ 2 }
                                   level={ Math.max(level, station.level) } collapsed={ collapsed } levelName={ levelName }
                                   onToggle={ handleToggle } onOpen={ onOpenCourse } onNotes={ onNotes }/>
                )) }
                { !tree.children.length && (
                    <Typography variant="body2" sx={ { color: '#4F5B67' } }>Nothing is planned inside it yet.</Typography>
                ) }
            </Stack>
        </Stack>
    );
}
